import { useMemo, useState } from 'react'
import {
  PieChart,
  Pie as RechartsPie,
  ResponsiveContainer,
  Sector,
} from 'recharts'

const Pie = RechartsPie as any

interface ProblemNoteItem {
  label: string
  value: number
}

interface Props {
  data?: ProblemNoteItem[]
}

const PALETTE = [
  '#EF4444',
  '#0EA5E9',
  '#8B5CF6',
  '#F97316',
  '#10B981',
  '#EAB308',
  '#EC4899',
]

const GRAY_COLOR = '#E5E7EB'

const DEFAULT_CATEGORIES: ProblemNoteItem[] = [
  { label: 'Chậm trễ', value: 0 },
  { label: 'Thủ tục rườm rà', value: 0 },
  { label: 'Thiếu minh bạch', value: 0 },
  { label: 'Công nghệ chưa ổn định', value: 0 },
]

export function ReviewStatsChart({ data = [] }: Props) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const activeData = useMemo(() => {
    return data.length > 0 ? data : DEFAULT_CATEGORIES
  }, [data])

  const total = useMemo(
    () => activeData.reduce((acc, curr) => acc + curr.value, 0),
    [activeData],
  )

  const isZero = total === 0

  const chartData = useMemo(() => {
    return activeData.map((item, index) => ({
      name: item.label,
      value: item.value,
      color: PALETTE[index % PALETTE.length],
      fill: PALETTE[index % PALETTE.length],
    }))
  }, [activeData])

  const pieData = useMemo(() => {
    if (isZero) {
      return [{ name: 'Empty', value: 1, fill: GRAY_COLOR }]
    }
    return chartData
  }, [chartData, isZero])

  const renderSector = (props: any) => (
    <Sector {...props} style={{ outline: 'none' }} className="cursor-pointer" />
  )

  const renderActiveSector = (props: any) => (
    <Sector
      {...props}
      style={{ outline: 'none' }}
      className="cursor-pointer transition-all duration-200 opacity-90"
    />
  )

  // Render Label line + %
  const renderCustomLabel = (props: any) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, value, index } = props

    if (index !== activeIndex || isZero) return null

    const angle = pieData.length === 1 || midAngle === undefined ? 45 : midAngle
    const RADIAN = Math.PI / 180
    const angleInRad = -angle * RADIAN

    const middleRadius = innerRadius + (outerRadius - innerRadius) / 2
    const startX = cx + middleRadius * Math.cos(angleInRad)
    const startY = cy + middleRadius * Math.sin(angleInRad)

    const breakRadius = outerRadius + 18
    const breakX = cx + breakRadius * Math.cos(angleInRad)
    const breakY = cy + breakRadius * Math.sin(angleInRad)

    const isRight = Math.cos(angleInRad) >= 0
    const lineLength = 55
    const endX = breakX + (isRight ? lineLength : -lineLength)
    const endY = breakY

    const percentStr = `${Math.round((value / total) * 100)}%`

    return (
      <g className="pointer-events-none">
        <path
          d={`M${startX},${startY} L${breakX},${breakY} L${endX},${endY}`}
          stroke="#1F2937"
          strokeWidth={1.2}
          fill="none"
        />

        <circle cx={startX} cy={startY} r={3} fill="#1F2937" />

        <circle cx={endX} cy={endY} r={3} fill="#1F2937" />

        <text
          x={breakX + (isRight ? lineLength / 2 : -lineLength / 2)}
          y={endY - 6}
          fill="#1F2937"
          textAnchor="middle"
          className="text-[14px] font-bold"
        >
          {percentStr}
        </text>
      </g>
    )
  }
  return (
    <div className="flex h-full w-full flex-col rounded-2xl border border-solid border-gray-300 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between ml-3 mb-4">
        <h3 className="text-[18px] font-bold uppercase text-[#1F2937]">
          Đánh giá theo loại phản ánh
        </h3>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center gap-6 md:flex-row md:justify-around">
        {/* Khối biểu đồ Donut */}
        <div className="relative flex h-[280px] w-[340px] items-center justify-center">
          <ResponsiveContainer
            width="100%"
            height="100%"
            initialDimension={{ width: 340, height: 280 }}
          >
            <PieChart style={{ overflow: 'visible' }}>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={70}
                outerRadius={100}
                dataKey="value"
                stroke="none"
                isAnimationActive={false}
                activeIndex={activeIndex ?? undefined}
                activeShape={renderActiveSector}
                shape={renderSector}
                labelLine={false}
                label={renderCustomLabel}
                onMouseEnter={(_: any, index: number) =>
                  !isZero && setActiveIndex(index)
                }
                onMouseLeave={() => setActiveIndex(null)}
              />
            </PieChart>
          </ResponsiveContainer>

          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[22px] font-bold text-[#1E2F41]">
              {total}
            </span>
            <span className="text-[13px] text-[#1E2F41]">lượt</span>
          </div>
        </div>

        <div className="flex w-full max-w-[260px] flex-col gap-3 text-xs">
          {chartData.map((item, i) => (
            <div
              key={i}
              role="button"
              tabIndex={0}
              onMouseEnter={() => !isZero && setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(null)}
              onFocus={() => !isZero && setActiveIndex(i)}
              onBlur={() => setActiveIndex(null)}
              className={`flex cursor-pointer items-center justify-between text-[14px] transition-all rounded-lg p-1.5 ${
                activeIndex === i ? 'bg-gray-100 font-semibold' : ''
              }`}
            >
              <div className="flex items-center gap-3 truncate">
                <span
                  className="h-3 w-3 shrink-0 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="truncate text-[#1E2F41]">{item.name}:</span>
              </div>
              <span className="font-bold text-[#1E2F41]">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
